import DayjsUtils from "@date-io/dayjs";
import { useMaskito } from "@maskito/react";
import { InputAdornment, TextField, withStyles } from "@material-ui/core";
import { Error } from "@material-ui/icons";
import {
  DatePicker as MuiDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import React, { forwardRef, useImperativeHandle } from "react";
import CalendarDotsIcon from "../icons/CalendarDotsIcon";
import ClockIcon from "../icons/ClockIcon";

const styles = {
  root: {
    "& .Mui-disabled": {
      cursor: "not-allowed",
    },
    "& label.Mui-focused": {
      color: "#5E6368",
      borderColor: "#5E6368",
      transform: "translate(0px, -24px) ",
    },
    "& label.MuiFormLabel-filled": {
      transform: "translate(0px, -24px) ",
    },

    "& .MuiInputLabel-formControl": {
      transform: "translate(0px, -24px) ",
    },

    // Override default margin for standard variant
    "& .MuiInput-formControl": {
      marginTop: 0,
    },

    "& .MuiOutlinedInput-root": {
      background: "#F9F9F9",
      color: "#5E6368",
      paddingBlock: "2.5px",
      "&.Mui-focused": {
        background: "#F1F5FE",
      },
      "&.Mui-focused fieldset ": {
        borderColor: "#3339FF",
        borderWidth: "1px",
      },
      borderColor: "#5E6368",
      borderRadius: "10px",
      "&.Mui-error": {
        background: "#FEF1F1",
        borderColor: "#EF2C2C",
      },

      "& fieldset": {
        top: 0,
      },
      "& legend": {
        display: "none",
      },
      "&.Mui-disabled": {
        background: "#e3e3e3",
        color: "#555555",
        cursor: "not-allowed",
      },
      "& .Mui-disabled ": {
        color: "#555555",
      },
    },

    "& .MuiInputLabel-asterisk": {
      color: "#EF2C2C",
    },

    "& .MuiFormHelperText-root": {
      marginLeft: "0",
      color: "#EF2C2C",
      fontSize: "12px",
      lineHeight: "8px",
    },

    // Add styles for informational variant
    "&.informational": {
      "& .MuiInputLabel-root": {
        color: "#414651",
      },
      "& .MuiInput-underline.Mui-disabled:before": {
        borderBottomStyle: "solid",
        borderBottomColor: "#D5D7DA",
      },

      "& input": {
        paddingLeft: "0px !important",
      },
    },

    "& input, & textarea": {
      caretColor: "#3339FF",
      color: "black",
      padding: "10.5px",
      "&::placeholder": {
        opacity: 1,
        color: "#9e9e9e",
        fontWeight: 400,
      },
      // Style time input specifically for iOS
      "&[type='time']": {
        "-webkit-appearance": "none",
        appearance: "none",
        padding: "10.5px",
        textAlign: "left",
        // height: "42px",
        "&::-webkit-calendar-picker-indicator": {
          display: "none",
        },
        "&::-webkit-datetime-edit": {
          padding: "0",
        },
        "&::-webkit-datetime-edit-fields-wrapper": {
          padding: "0",
        },
        "&::-webkit-date-and-time-value": {
          textAlign: "left",
        },
      },
    },
    "& .MuiOutlinedInput-multiline": {
      padding: "0",
    },
  },
};

const CustomTextField = withStyles(styles)(TextField);
const CustomDatePicker = withStyles(styles)(MuiDatePicker);

/**
 * A styled input component that extends Material-UI's TextField with custom styling and additional features.
 * Supports various input types including text, date, phone, and time with consistent styling across variants.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.helperText="Required"] - Text shown below the input when error is true
 * @param {boolean} [props.error=false] - Whether to show the input in an error state
 * @param {boolean} [props.showErrorIcon=true] - Whether to show an error icon next to the helper text when error is true
 * @param {('text'|'date'|'phone'|'time')} [props.type="text"] - The type of input to render
 * @param {string} props.label - The label text for the input
 * @param {Function} props.onChange - Callback fired when the input value changes
 * @param {Function} props.onBlur - Callback fired when the input loses focus
 * @param {string} props.placeholder - Placeholder text shown when the input is empty
 * @param {React.ReactNode} props.icon - Icon to show at the end of the input
 * @param {string} [props.labelClasses=""] - Additional CSS classes to apply to the label
 * @param {boolean} [props.isInformational=false] - When true, shows black label and solid underline for disabled fields
 * @param {('outlined'|'standard'|'filled')} [props.variant="outlined"] - The Material-UI input variant to use
 * @param {boolean} [props.disabled] - Whether the input is disabled
 * @param {boolean} [props.required] - Whether the input is required
 * @param {string} [props.value] - The controlled value of the input
 * @param {string} [props.defaultValue] - The default value of the input when uncontrolled
 * @param {boolean} [props.multiline] - Whether the input should accept multiple lines of text
 * @param {number} [props.rows] - Number of rows to show for multiline inputs
 * @param {string} [props.name] - The name attribute of the input
 * @returns {React.ReactElement}
 */

const StyledInput = forwardRef(
  (
    {
      helperText = "Required",
      error = false,
      showErrorIcon = true,
      type = "text",
      label,
      onChange,
      onBlur,
      placeholder,
      icon,
      labelClasses = "",
      isInformational = false,
      variant = "outlined",
      ...props
    },
    ref
  ) => {
    const phoneInputRef = useMaskito({
      options: {
        mask: [
          "(",
          /\d/,
          /\d/,
          /\d/,
          ")",
          " ",
          /\d/,
          /\d/,
          /\d/,
          "-",
          /\d/,
          /\d/,
          /\d/,
          /\d/,
        ],
      },
    });

    const rootClassName = `justify-end h-full ${isInformational ? "informational" : ""}`;

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (phoneInputRef.current) {
          phoneInputRef.current.focus();
        }
      },
    }));

    if (type === "date") {
      return (
        <MuiPickersUtilsProvider utils={DayjsUtils}>
          <CustomDatePicker
            autoOk
            error={error}
            inputVariant="outlined"
            label={label}
            orientation="portrait"
            size="small"
            {...props}
            className={rootClassName}
            InputLabelProps={{
              className: labelClasses,
              shrink: true,
            }}
            InputProps={{
              placeholder: placeholder,
              endAdornment: (
                <InputAdornment position="end">
                  <CalendarDotsIcon className="w-24" />
                </InputAdornment>
              ),
              inputProps: {
                ref: ref,
              },
            }}
            clearable
            format="MM/DD/YYYY"
            helperText={
              error ? (
                <>
                  {showErrorIcon && error ? (
                    <Error className="w-20 mr-2" />
                  ) : null}
                  {helperText}
                </>
              ) : null
            }
            onBlur={onBlur}
            onChange={onChange}
          />
        </MuiPickersUtilsProvider>
      );
    }
    if (type === "phone") {
      return (
        <CustomTextField
          label={label}
          variant={variant}
          {...props}
          className={rootClassName}
          InputLabelProps={{
            shrink: true,
          }}
          error={error}
          helperText={
            error ? (
              <>
                {showErrorIcon && error ? (
                  <Error className="w-20 mr-2" />
                ) : null}
                {helperText}
              </>
            ) : null
          }
          inputProps={{
            ref: phoneInputRef,
            onBlur: onBlur,
            placeholder: placeholder,
          }}
          onInput={onChange}
          size="small"
        />
      );
    }

    if (type === "time") {
      return (
        <CustomTextField
          label={label}
          type={type}
          variant={variant}
          {...props}
          className={rootClassName}
          InputLabelProps={{
            shrink: true,
            className: labelClasses,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <ClockIcon className="w-24" />
              </InputAdornment>
            ),
          }}
          error={error}
          helperText={
            error ? (
              <>
                {showErrorIcon && error ? (
                  <Error className="w-20 mr-2" />
                ) : null}
                {helperText}
              </>
            ) : null
          }
          inputProps={{
            ref: ref,
            onChange: onChange,
            onBlur: onBlur,
            placeholder: placeholder,
          }}
          size="small"
        />
      );
    }

    return (
      <CustomTextField
        label={label}
        type={type}
        variant={variant}
        {...props}
        className={rootClassName}
        InputLabelProps={{
          shrink: true,
          className: labelClasses,
        }}
        InputProps={{
          endAdornment: icon ? (
            <InputAdornment position="end">{icon}</InputAdornment>
          ) : null,
        }}
        error={error}
        helperText={
          error ? (
            <>
              {showErrorIcon && error ? <Error className="w-20 mr-2" /> : null}
              {helperText}
            </>
          ) : null
        }
        inputProps={{
          ref: ref,
          onChange: onChange,
          onBlur: onBlur,
          placeholder: placeholder,
        }}
        size="small"
      />
    );
  }
);

StyledInput.displayName = "StyledInput";

export default StyledInput;
